import { useEffect, useMemo, useRef, useState } from 'react'
import { useSWRConfig } from 'swr'
import {
  EventTicket,
  createCoupon,
  parseDuplicatedCode,
  saveCoupons,
  saveCouponDiscounts,
} from '../../Service/coupons'
import {
  useCouponDiscounts,
  useCoupons,
  useEventTickets,
} from '../../hooks/coupons'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import {
  buildCouponLink,
  generateCouponCode,
  toDateTimeLocal,
} from '../../utils/coupon-code'
import { downloadCsv } from '../../utils/download-file'
import Loader from '../Loader'
import ModalShell from '../ModalShell'
import {
  CardList,
  CardMeta,
  CodeCell,
  CodeInput,
  CodeTag,
  CouponCard,
  CouponCardTitle,
  CouponName,
  EmptyState,
  ErrorMessage,
  Field,
  FieldLabel,
  FieldPair,
  Footer,
  FooterActions,
  GhostButton,
  Hint,
  Input,
  LinkButton,
  LoadingArea,
  NewCouponBox,
  PrimaryButton,
  ScrollArea,
  Select,
  SoftButton,
  Step,
  StepLabel,
  StepNumber,
  StepSeparator,
  Stepper,
  SuccessBox,
  SuccessSummary,
  SuccessText,
  SuccessTitle,
  Table,
  Toolbar,
  ToolbarButtons,
  ToolbarHint,
  WizardContainer,
} from './styles'

interface CouponRow {
  id: number
  nome: string
  codigo: string
  limite: string
  validade: string
}

interface DiscountRow {
  comissarioId: number
  nome: string
  porcentagem: string
  valor: string
}

interface CouponsWizardProps {
  eventId: string
  onClose: () => void
}

const STEPS = ['Cupons', 'Descontos por ingresso', 'Concluído']

const changedDiscountRows = (rows: DiscountRow[], baseline: string) => {
  const baseById = new Map(
    (JSON.parse(baseline) as DiscountRow[]).map((row) => [row.comissarioId, row])
  )
  return rows.filter((row) => {
    const base = baseById.get(row.comissarioId)
    return (
      base == null ||
      base.porcentagem !== row.porcentagem ||
      base.valor !== row.valor
    )
  })
}
const DESKTOP_QUERY = '(min-width: 768px)'

export const CouponsWizard = ({ eventId, onClose }: CouponsWizardProps) => {
  // Mobile-first: cards são o padrão; a tabela é o upgrade no desktop.
  const isDesktop = useMediaQuery(DESKTOP_QUERY)
  const { mutate } = useSWRConfig()

  const [step, setStep] = useState<number>(1)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [duplicatedCode, setDuplicatedCode] = useState<string | null>(null)

  // Etapa 1
  const { coupons, slug, couponsLoading, refetchCoupons } = useCoupons(eventId)
  const [rows, setRows] = useState<CouponRow[]>([])
  const [couponsBaseline, setCouponsBaseline] = useState<CouponRow[]>([])
  const baselineRef = useRef<CouponRow[]>([])
  // Depois de salvar, quem manda é o backend: ele normaliza código, limite e
  // validade, então a resposta dele substitui a tela em vez de ser mesclada.
  const acceptServerRef = useRef<boolean>(false)
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [newName, setNewName] = useState<string>('')

  // Etapa 2
  const { tickets, ticketsLoading } = useEventTickets(eventId)
  const [ticketId, setTicketId] = useState<number | null>(null)
  const { discounts, discountsLoading } = useCouponDiscounts(eventId, ticketId)
  const [discountRows, setDiscountRows] = useState<DiscountRow[]>([])
  const [discountsBaseline, setDiscountsBaseline] = useState<string>('[]')
  // Ingressos cujos descontos já foram salvos nesta sessão do wizard.
  const [savedTickets, setSavedTickets] = useState<number[]>([])
  const [drafts, setDrafts] = useState<Record<number, DiscountRow[]>>({})
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const [lastSaved, setLastSaved] = useState<
    { ticketId: number; applied: number | null }[]
  >([])

  const [copiedId, setCopiedId] = useState<number | null>(null)

  /**
   * Carrega os cupons do backend no estado editável da etapa 1.
   *
   * Mescla em vez de sobrescrever: qualquer revalidação do SWR — inclusive o
   * refetch disparado por "Novo cupom" — apagava tudo que o produtor tinha
   * digitado e ainda não salvo, fazendo a correção dele "sumir" da tela.
   * Linha editada permanece; o resto acompanha o backend.
   */
  useEffect(() => {
    const mapped = coupons.map((coupon) => ({
      id: coupon.id,
      nome: coupon.nome,
      codigo: coupon.codigo ?? '',
      limite: coupon.limite != null ? String(coupon.limite) : '',
      validade: toDateTimeLocal(coupon.validade),
    }))

    if (acceptServerRef.current) {
      acceptServerRef.current = false
      setRows(mapped)
    } else {
      setRows((current) => {
        const localById = new Map(current.map((row) => [row.id, row]))
        const baseById = new Map(baselineRef.current.map((row) => [row.id, row]))

        return mapped.map((row) => {
          const local = localById.get(row.id)
          const base = baseById.get(row.id)
          const edited =
            local != null &&
            base != null &&
            JSON.stringify(local) !== JSON.stringify(base)
          return edited ? local : row
        })
      })
    }

    baselineRef.current = mapped
    setCouponsBaseline(mapped)
  }, [coupons])

  // Seleciona o primeiro ingresso assim que a lista chega.
  useEffect(() => {
    if (ticketId === null && tickets.length > 0) {
      setTicketId(tickets[0].id)
    }
  }, [tickets, ticketId])

  // Espelha os descontos do ingresso selecionado no estado editável.
  useEffect(() => {
    const mapped = discounts.map((discount) => ({
      comissarioId: discount.id,
      nome: discount.nome,
      porcentagem:
        discount.porcentagem != null && Number(discount.porcentagem) > 0
          ? String(discount.porcentagem)
          : '',
      valor:
        discount.valor != null && Number(discount.valor) > 0
          ? String(discount.valor)
          : '',
    }))
    const draft = ticketId != null ? draftsRef.current[ticketId] : undefined
    const draftById = new Map(
      (draft ?? []).map((row) => [row.comissarioId, row])
    )
    setDiscountRows(
      mapped.map((row) => {
        const edited = draftById.get(row.comissarioId)
        return edited
          ? { ...row, porcentagem: edited.porcentagem, valor: edited.valor }
          : row
      })
    )
    setDiscountsBaseline(JSON.stringify(mapped))
  }, [discounts, ticketId])

  // Ingressos agrupados pelo nome do setor, preservando a ordem do backend.
  // Facilita o produtor/operador a localizar o ingresso na lista.
  const ticketGroups = useMemo(() => {
    const groups: { setor: string; tickets: EventTicket[] }[] = []
    tickets.forEach((ticket) => {
      const setor = ticket.setor?.trim() || 'Sem setor'
      const last = groups[groups.length - 1]
      if (last && last.setor === setor) {
        last.tickets.push(ticket)
      } else {
        groups.push({ setor, tickets: [ticket] })
      }
    })
    return groups
  }, [tickets])

  const usadosById = useMemo(() => {
    const map = new Map<number, number>()
    coupons.forEach((coupon) => map.set(coupon.id, coupon.usados ?? 0))
    return map
  }, [coupons])

  const activeCoupons = useMemo(
    () => rows.filter((row) => row.codigo.trim() !== ''),
    [rows]
  )

  // Edições pendentes: o estado atual difere do que veio do backend?
  const couponsDirty = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(couponsBaseline),
    [rows, couponsBaseline]
  )

  const discountsDirty = useMemo(
    () => JSON.stringify(discountRows) !== discountsBaseline,
    [discountRows, discountsBaseline]
  )

  /**
   * Código repetido na própria tela — detectado enquanto o produtor digita, sem
   * ida ao servidor. As duas linhas ficam marcadas (nenhuma das duas está
   * certa) e o "Salvar" fica bloqueado até sobrar um cupom por código.
   */
  const duplicatedCodes = useMemo(() => {
    const count = new Map<string, number>()
    rows.forEach((row) => {
      const code = row.codigo.trim().toUpperCase()
      if (code === '') return
      count.set(code, (count.get(code) ?? 0) + 1)
    })
    return new Set(
      Array.from(count.entries())
        .filter(([, total]) => total > 1)
        .map(([code]) => code)
    )
  }, [rows])

  // Linhas em erro, por id: as repetidas na tela + a recusada pelo servidor.
  const errorRowIds = useMemo(() => {
    const ids = new Set<number>()
    rows.forEach((row) => {
      const code = row.codigo.trim().toUpperCase()
      if (code === '') return
      if (duplicatedCodes.has(code) || code === duplicatedCode) ids.add(row.id)
    })
    return ids
  }, [rows, duplicatedCodes, duplicatedCode])

  const pendingTicketIds = useMemo(() => {
    const ids = new Set(Object.keys(drafts).map(Number))
    if (ticketId != null) {
      if (discountsDirty) ids.add(ticketId)
      else ids.delete(ticketId)
    }
    return ids
  }, [drafts, ticketId, discountsDirty])

  const stashCurrentDraft = () =>
    setDrafts((current) => {
      if (ticketId == null) return current
      const { [ticketId]: _previous, ...others } = current
      return discountsDirty
        ? {
            ...others,
            [ticketId]: changedDiscountRows(discountRows, discountsBaseline),
          }
        : others
    })

  const handleTicketChange = (nextTicketId: number) => {
    if (nextTicketId === ticketId) return

    stashCurrentDraft()
    setError(null)
    setTicketId(nextTicketId)
  }

  const handleClose = () => {
    if (
      pendingTicketIds.size > 0 &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Há descontos não salvos em ${pendingTicketIds.size} ingresso(s). Fechar vai descartá-los. Continuar?`
      )
    ) {
      return
    }
    onClose()
  }

  const revalidateDiscounts = () =>
    mutate(
      (key) =>
        Array.isArray(key) &&
        key[0] === 'couponDiscounts' &&
        key[1] === eventId
    )

  /** Atalho da etapa 1: vai para os descontos sem salvar os cupons. */
  const handleSkipToDiscounts = () => {
    if (
      couponsDirty &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        'Você tem alterações não salvas nos cupons. Pular para os descontos vai descartá-las. Continuar?'
      )
    ) {
      return
    }

    setError(null)
    setDuplicatedCode(null)
    setStep(2)
  }

  const updateRow = (id: number, field: keyof CouponRow, value: string) => {
    setDuplicatedCode(null)
    setError(null)
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    )
  }

  /** XOR no input: preencher % limpa R$ e vice-versa. */
  const updateDiscount = (
    comissarioId: number,
    field: 'porcentagem' | 'valor',
    value: string
  ) => {
    const other = field === 'porcentagem' ? 'valor' : 'porcentagem'
    setDiscountRows((current) =>
      current.map((row) =>
        row.comissarioId === comissarioId
          ? { ...row, [field]: value, [other]: value !== '' ? '' : row[other] }
          : row
      )
    )
  }

  const applyToAll = (field: 'porcentagem' | 'valor') => {
    const label = field === 'porcentagem' ? '%' : 'R$'
    // eslint-disable-next-line no-alert
    const value = window.prompt(
      `Desconto em ${label} para todos os cupons listados:`
    )
    if (value === null) return

    const other = field === 'porcentagem' ? 'valor' : 'porcentagem'
    setDiscountRows((current) =>
      current.map((row) => ({ ...row, [field]: value, [other]: '' }))
    )
  }

  const handleCreateCoupon = async () => {
    const name = newName.trim()
    if (!name) return

    setSaving(true)
    setError(null)
    try {
      await createCoupon(eventId, name)
      await refetchCoupons()
      setNewName('')
      setIsCreating(false)
    } catch (err) {
      setError('Não foi possível criar o cupom. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  /** Baixa os cupons ativos (nome, código e link) em CSV. */
  const handleDownloadCsv = () => {
    if (!slug) return
    const rows: string[][] = [
      ['Cupom', 'Código', 'Link'],
      ...activeCoupons.map((row) => [
        row.nome,
        row.codigo,
        buildCouponLink(slug, row.id),
      ]),
    ]
    downloadCsv(rows, 'cupons.csv')
  }

  const handleCopyLink = (comissarioId: number) => {
    if (!slug) return
    navigator.clipboard?.writeText(buildCouponLink(slug, comissarioId))
    setCopiedId(comissarioId)
    setTimeout(() => setCopiedId(null), 1500)
  }

  const handleSaveCoupons = async () => {
    setSaving(true)
    setError(null)
    setDuplicatedCode(null)

    try {
      await saveCoupons(
        eventId,
        rows.map((row) => ({
          id: row.id,
          codigo: row.codigo.trim() || null,
          limite: row.limite !== '' ? Number(row.limite) : null,
          validade: row.validade || null,
        }))
      )
      acceptServerRef.current = true
      await Promise.all([refetchCoupons(), revalidateDiscounts()])
      setStep(2)
    } catch (err) {
      const code = parseDuplicatedCode(err)
      if (code) {
        setDuplicatedCode(code)
        setError(
          `O código "${code}" já está em uso por outro cupom neste evento.`
        )
      } else {
        setError('Não foi possível salvar os cupons. Tente novamente.')
      }
    } finally {
      setSaving(false)
    }
  }

  const ticketName = (id: number) =>
    tickets.find((ticket) => ticket.id === id)?.nome ?? '—'

  const handleSaveDiscounts = async () => {
    if (!ticketId) return

    const toSave: Record<number, DiscountRow[]> = { ...drafts }
    if (discountsDirty) {
      toSave[ticketId] = changedDiscountRows(discountRows, discountsBaseline)
    } else if (Object.keys(toSave).length === 0) {
      toSave[ticketId] = discountRows
    }

    setSaving(true)
    setError(null)
    const saved: number[] = []
    try {
      for (const [id, rowsToSave] of Object.entries(toSave)) {
        await saveCouponDiscounts(
          eventId,
          Number(id),
          rowsToSave.map((row) => ({
            comissarioId: row.comissarioId,
            porcentagem: row.porcentagem !== '' ? Number(row.porcentagem) : null,
            valor: row.valor !== '' ? Number(row.valor) : null,
          }))
        )
        saved.push(Number(id))
      }
      setDrafts({})
      setDiscountsBaseline(JSON.stringify(discountRows))
      setLastSaved(
        saved.map((id) => ({
          ticketId: id,
          applied:
            id === ticketId
              ? discountRows.filter(
                  (row) => row.porcentagem !== '' || row.valor !== ''
                ).length
              : null,
        }))
      )
      setStep(3)
    } catch (err) {
      const failed = Object.keys(toSave)
        .map(Number)
        .find((id) => !saved.includes(id))
      setDrafts((current) => {
        const next = { ...current }
        saved.forEach((id) => delete next[id])
        return next
      })
      if (saved.includes(ticketId)) {
        setDiscountsBaseline(JSON.stringify(discountRows))
      }
      setError(
        `Não foi possível salvar os descontos do ingresso "${ticketName(
          failed ?? ticketId
        )}". Tente novamente.`
      )
    } finally {
      if (saved.length > 0) {
        setSavedTickets((current) =>
          Array.from(new Set([...current, ...saved]))
        )
        revalidateDiscounts()
      }
      setSaving(false)
    }
  }

  /* ---------- campos reaproveitados entre card (mobile) e tabela ---------- */

  const codeField = (row: CouponRow) => (
    <CodeCell>
      <CodeInput
        maxLength={20}
        value={row.codigo}
        hasError={errorRowIds.has(row.id)}
        placeholder="código"
        onChange={(e) =>
          updateRow(row.id, 'codigo', e.target.value.toUpperCase())
        }
      />
      <SoftButton
        onClick={() => updateRow(row.id, 'codigo', generateCouponCode())}
      >
        Gerar
      </SoftButton>
    </CodeCell>
  )

  const limitField = (row: CouponRow) => (
    <Input
      type="number"
      min={0}
      value={row.limite}
      placeholder="∞"
      onChange={(e) => updateRow(row.id, 'limite', e.target.value)}
    />
  )

  const validityField = (row: CouponRow) => (
    <Input
      type="datetime-local"
      value={row.validade}
      onChange={(e) => updateRow(row.id, 'validade', e.target.value)}
    />
  )

  const copyButton = (id: number, disabled = false) => (
    <SoftButton onClick={() => handleCopyLink(id)} disabled={!slug || disabled}>
      {copiedId === id ? '✓ Copiado!' : 'Copiar link'}
    </SoftButton>
  )

  const percentField = (row: DiscountRow) => (
    <Input
      type="number"
      min={0}
      max={100}
      step="0.01"
      placeholder="0"
      value={row.porcentagem}
      disabled={saving}
      onChange={(e) =>
        updateDiscount(row.comissarioId, 'porcentagem', e.target.value)
      }
    />
  )

  const valueField = (row: DiscountRow) => (
    <Input
      type="number"
      min={0}
      step="0.01"
      placeholder="0,00"
      value={row.valor}
      disabled={saving}
      onChange={(e) => updateDiscount(row.comissarioId, 'valor', e.target.value)}
    />
  )

  return (
    <ModalShell
      title="Cupons de desconto"
      onClose={handleClose}
      maxWidth={900}
    >
      <WizardContainer>
        <Stepper>
        {STEPS.map((label, index) => {
          const number = index + 1
          return (
            <Step key={label} active={step === number} done={step > number}>
              <StepNumber active={step === number} done={step > number}>
                {number}
              </StepNumber>
              <StepLabel active={step === number}>{label}</StepLabel>
              {number < STEPS.length && <StepSeparator />}
            </Step>
          )
        })}
      </Stepper>

      {/* ---------------- ETAPA 1 ---------------- */}
      {step === 1 && (
        <>
          <Toolbar>
            <ToolbarHint>
              Defina <b>código</b>, <b>limite de uso</b> e <b>validade</b> dos
              cupons deste evento.
            </ToolbarHint>
            <SoftButton
              onClick={() => setIsCreating((current) => !current)}
              disabled={saving}
            >
              + Novo cupom
            </SoftButton>
          </Toolbar>

          {isCreating && (
            <NewCouponBox>
              <div>
                <label htmlFor="novo-cupom">Nome do cupom</label>
                <Input
                  id="novo-cupom"
                  value={newName}
                  placeholder="Ex.: PARCEIRO RÁDIO FM"
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <PrimaryButton
                onClick={handleCreateCoupon}
                disabled={saving || !newName.trim()}
              >
                Criar cupom
              </PrimaryButton>
              <GhostButton
                onClick={() => {
                  setIsCreating(false)
                  setNewName('')
                }}
                disabled={saving}
              >
                Cancelar
              </GhostButton>
            </NewCouponBox>
          )}

          {couponsLoading && (
            <LoadingArea>
              <Loader noMargin />
            </LoadingArea>
          )}

          {!couponsLoading && rows.length === 0 && (
            <EmptyState>
              Nenhum cupom cadastrado. Clique em “Novo cupom” para começar.
            </EmptyState>
          )}

          {/* Mobile: um card por cupom */}
          {!couponsLoading && rows.length > 0 && !isDesktop && (
            <CardList>
              {rows.map((row) => (
                <CouponCard key={row.id}>
                  <CouponCardTitle>{row.nome}</CouponCardTitle>

                  <Field>
                    <FieldLabel>Código</FieldLabel>
                    {codeField(row)}
                  </Field>

                  <FieldPair>
                    <Field>
                      <FieldLabel>Limite de uso</FieldLabel>
                      {limitField(row)}
                    </Field>
                    <Field>
                      <FieldLabel>Validade</FieldLabel>
                      {validityField(row)}
                    </Field>
                  </FieldPair>

                  <CardMeta>Usados: {usadosById.get(row.id) ?? 0}</CardMeta>
                  {copyButton(row.id, row.codigo.trim() === '')}
                </CouponCard>
              ))}
            </CardList>
          )}

          {/* Desktop: tabela */}
          {!couponsLoading && rows.length > 0 && isDesktop && (
            <ScrollArea>
              <Table>
                <thead>
                  <tr>
                    <th>Cupom</th>
                    <th>Código</th>
                    <th>Limite de uso</th>
                    <th>Validade</th>
                    <th>Usados</th>
                    <th>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <CouponName>{row.nome}</CouponName>
                      <td>{codeField(row)}</td>
                      <td>{limitField(row)}</td>
                      <td>{validityField(row)}</td>
                      <td>{usadosById.get(row.id) ?? 0}</td>
                      <td>{copyButton(row.id, row.codigo.trim() === '')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          )}

          <Hint>
            Só os cupons <b>com código</b> aparecem na próxima etapa para receber
            desconto. Deixe sem código para manter o cupom cadastrado, mas
            inativo neste evento.
          </Hint>
        </>
      )}

      {/* ---------------- ETAPA 2 ---------------- */}
      {step === 2 && (
        <>
          <Toolbar>
            <Field>
              <FieldLabel>Ingresso</FieldLabel>
              <Select
                value={ticketId ?? ''}
                onChange={(e) => handleTicketChange(Number(e.target.value))}
                disabled={saving || ticketsLoading || tickets.length === 0}
              >
                {ticketGroups.map((group) => (
                  <optgroup key={group.setor} label={group.setor}>
                    {group.tickets.map((ticket) => (
                      <option key={ticket.id} value={ticket.id}>
                        {ticket.nome}
                        {pendingTicketIds.has(ticket.id)
                          ? ' • não salvo'
                          : savedTickets.includes(ticket.id)
                            ? ' ✓'
                            : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </Field>
            <ToolbarButtons>
              <GhostButton
                onClick={() => applyToAll('porcentagem')}
                disabled={saving}
              >
                Definir % p/ todos
              </GhostButton>
              <GhostButton
                onClick={() => applyToAll('valor')}
                disabled={saving}
              >
                Definir R$ p/ todos
              </GhostButton>
            </ToolbarButtons>
          </Toolbar>

          {(discountsLoading || ticketsLoading) && (
            <LoadingArea>
              <Loader noMargin />
            </LoadingArea>
          )}

          {!discountsLoading && discountRows.length === 0 && (
            <EmptyState>
              Nenhum cupom com código neste evento. Volte à etapa 1 e defina um
              código.
            </EmptyState>
          )}

          {/* Mobile: card por cupom, % e R$ lado a lado */}
          {!discountsLoading && discountRows.length > 0 && !isDesktop && (
            <CardList>
              {discountRows.map((row) => (
                <CouponCard key={row.comissarioId}>
                  <CouponCardTitle>{row.nome}</CouponCardTitle>
                  <FieldPair>
                    <Field>
                      <FieldLabel>Desconto (%)</FieldLabel>
                      {percentField(row)}
                    </Field>
                    <Field>
                      <FieldLabel>Desconto (R$)</FieldLabel>
                      {valueField(row)}
                    </Field>
                  </FieldPair>
                </CouponCard>
              ))}
            </CardList>
          )}

          {/* Desktop: tabela */}
          {!discountsLoading && discountRows.length > 0 && isDesktop && (
            <ScrollArea>
              <Table>
                <thead>
                  <tr>
                    <th>Cupom</th>
                    <th>Desconto (%)</th>
                    <th>Desconto (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {discountRows.map((row) => (
                    <tr key={row.comissarioId}>
                      <CouponName>{row.nome}</CouponName>
                      <td>{percentField(row)}</td>
                      <td>{valueField(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          )}

          <Hint>
            Por cupom vale <b>% OU R$</b> — preencher um zera o outro. Deixar os
            dois vazios remove o desconto desse cupom no ingresso. Pode trocar de
            ingresso à vontade: os descontos ficam guardados e{' '}
            <b>Salvar descontos</b> grava todos de uma vez.
          </Hint>
        </>
      )}

      {/* ---------------- ETAPA 3 ---------------- */}
      {step === 3 && (
        <SuccessBox>
          <SuccessTitle>Descontos salvos!</SuccessTitle>
          <SuccessText>
            Os cupons já estão valendo para o evento. A atualização dos PDVs/site
            é gerada automaticamente a cada 10 minutos.
          </SuccessText>

          <SuccessSummary>
            {lastSaved.length > 1 ? (
              <div>
                Ingressos salvos:{' '}
                <b>
                  {lastSaved.map((item) => ticketName(item.ticketId)).join(', ')}
                </b>
              </div>
            ) : (
              <div>
                Ingresso:{' '}
                <b>
                  {lastSaved[0] ? ticketName(lastSaved[0].ticketId) : '—'}
                </b>
              </div>
            )}
            <div>
              Cupons com código: <b>{activeCoupons.length}</b>
            </div>
            {lastSaved.length === 1 && lastSaved[0].applied != null && (
              <div>
                Cupons com desconto aplicado: <b>{lastSaved[0].applied}</b>
              </div>
            )}
            {savedTickets.length > 1 && (
              <div>
                Ingressos configurados nesta sessão:{' '}
                <b>{savedTickets.length}</b>
              </div>
            )}
          </SuccessSummary>

          {activeCoupons.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <SoftButton onClick={handleDownloadCsv} disabled={!slug}>
                ↓ Baixar cupons (CSV)
              </SoftButton>
            </div>
          )}

          {activeCoupons.length > 0 && !isDesktop && (
            <CardList style={{ marginTop: 20, textAlign: 'left' }}>
              {activeCoupons.map((row) => (
                <CouponCard key={row.id}>
                  <CouponCardTitle>{row.nome}</CouponCardTitle>
                  <CardMeta>
                    Código: <b>{row.codigo}</b>
                  </CardMeta>
                  {copyButton(row.id)}
                </CouponCard>
              ))}
            </CardList>
          )}

          {activeCoupons.length > 0 && isDesktop && (
            <ScrollArea style={{ marginTop: 20 }}>
              <Table>
                <thead>
                  <tr>
                    <th>Cupom</th>
                    <th>Código</th>
                    <th>Link de compra</th>
                  </tr>
                </thead>
                <tbody>
                  {activeCoupons.map((row) => (
                    <tr key={row.id}>
                      <CouponName>{row.nome}</CouponName>
                      <CodeTag>{row.codigo}</CodeTag>
                      <td>{copyButton(row.id)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          )}
        </SuccessBox>
      )}

      {step === 1 && duplicatedCodes.size > 0 && (
        <ErrorMessage>
          Código repetido em mais de um cupom:{' '}
          {Array.from(duplicatedCodes).join(', ')}. Deixe só um cupom com cada
          código para poder salvar.
        </ErrorMessage>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Footer>
        {step < 3 ? (
          <LinkButton onClick={handleClose}>Cancelar</LinkButton>
        ) : (
          <GhostButton onClick={() => setStep(1)}>Editar cupons</GhostButton>
        )}

        <FooterActions>
          {step === 2 && (
            <GhostButton onClick={() => setStep(1)} disabled={saving}>
              ← Voltar
            </GhostButton>
          )}
          {step === 1 && (
            <GhostButton onClick={handleSkipToDiscounts} disabled={saving}>
              Pular para descontos →
            </GhostButton>
          )}
          {step === 1 && (
            <PrimaryButton
              onClick={handleSaveCoupons}
              disabled={saving || duplicatedCodes.size > 0}
            >
              {saving ? 'Salvando...' : 'Salvar e avançar →'}
            </PrimaryButton>
          )}
          {step === 2 && (
            <PrimaryButton
              onClick={handleSaveDiscounts}
              disabled={saving || !ticketId || discountRows.length === 0}
            >
              {saving
                ? 'Salvando...'
                : pendingTicketIds.size > 1
                  ? `Salvar descontos (${pendingTicketIds.size} ingressos)`
                  : 'Salvar descontos'}
            </PrimaryButton>
          )}
          {step === 3 && (
            <GhostButton onClick={() => setStep(2)}>
              ← Editar outro ingresso
            </GhostButton>
          )}
          {step === 3 && (
            <PrimaryButton onClick={onClose}>Concluir</PrimaryButton>
          )}
        </FooterActions>
      </Footer>
      </WizardContainer>
    </ModalShell>
  )
}
